import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import BASE_URL from '../utils/api';
import { cpfValido, cnpjValido, mascaraCpf, mascaraCnpj, somenteDigitos, styles } from './AprovacaoOrcamento';

// Página pública do aceite final: mesmo modelo da aprovação do orçamento. Só abre com o link enviado pela empresa.
export default function AprovacaoProjeto() {
  const { id } = useParams();
  const params = new URLSearchParams(window.location.search);
  const tenantCnpj = params.get('tenantCnpj') || '';
  const token = params.get('token') || '';
  const consulta = `tenantCnpj=${encodeURIComponent(tenantCnpj)}`;

  const [projeto, setProjeto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [aceito, setAceito] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const carregar = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/AceiteProjeto/publico/${id}?${consulta}&token=${encodeURIComponent(token)}`);
      if (!res.ok) throw new Error();
      setProjeto(await res.json());
      setError('');
    } catch {
      setError('Não foi possível carregar o projeto. Verifique o link e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, [id]);

  const pessoaJuridica = projeto?.tipoPessoaCliente === 'PJ';
  const documentosValidos = cpfValido(cpf) && (!pessoaJuridica || cnpjValido(cnpj));

  const confirmar = async () => {
    if (!documentosValidos) { alert('Informe um documento válido.'); return; }
    try {
      setEnviando(true);
      const res = await fetch(`${BASE_URL}/AceiteProjeto/publico/${id}?${consulta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, cpf: somenteDigitos(cpf), cnpj: pessoaJuridica ? somenteDigitos(cnpj) : null })
      });
      if (!res.ok) {
        let mensagem = 'Erro ao confirmar a aprovação. Tente novamente.';
        try { mensagem = (await res.json()).erro || mensagem; } catch { /* resposta sem JSON */ }
        throw new Error(mensagem);
      }
      await carregar();
    } catch (err) {
      alert(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const Aviso = ({ icone, titulo, cor, children }) => (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.successContainer}>
          <span style={{ fontSize: 56, marginBottom: 16 }}>{icone}</span>
          <h2 style={{ color: cor, marginBottom: 8 }}>{titulo}</h2>
          {children}
        </div>
      </div>
    </div>
  );

  if (loading) return <Aviso icone="⏳" titulo="Carregando projeto..." cor="#666" />;
  if (error) return <Aviso icone="⚠️" titulo="Projeto não encontrado" cor="#d32f2f"><p style={{ color: '#666' }}>{error}</p></Aviso>;

  if (!projeto.jaAceito && !projeto.disponivel) {
    return (
      <Aviso icone="🔧" titulo="Aprovação indisponível" cor="rgb(184, 98, 0)">
        <p style={{ color: '#666' }}>O projeto #{projeto.projetoId} ainda tem etapas em andamento. Fale com {projeto.nomeEmpresa}.</p>
      </Aviso>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>Aprovação Final do Projeto</h1>
          <p style={styles.headerSubtitle}>Projeto #{projeto.projetoId}{projeto.titulo ? ` - ${projeto.titulo}` : ''}</p>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Dados do Projeto</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}><span style={styles.infoLabel}>Empresa:</span><span style={styles.infoValue}>{projeto.nomeEmpresa || '-'}</span></div>
            <div style={styles.infoItem}><span style={styles.infoLabel}>Cliente:</span><span style={styles.infoValue}>{projeto.nomeCliente || '-'}</span></div>
            <div style={styles.infoItem}><span style={styles.infoLabel}>Prazo de Garantia:</span><span style={styles.infoValue}>{projeto.prazoGarantiaMeses ? `${projeto.prazoGarantiaMeses} meses a partir da aprovação` : '-'}</span></div>
            <div style={styles.infoItem}><span style={styles.infoLabel}>Data de Aprovação:</span><span style={styles.infoValue}>{projeto.dataAceite ? new Date(projeto.dataAceite).toLocaleDateString('pt-BR') : 'Aguardando sua aprovação'}</span></div>
          </div>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>O que foi entregue</h3>
          <div style={styles.tabelaContainer}>
            <table style={styles.tabela}>
              <thead><tr><th style={styles.th}>Data da entrega</th><th style={styles.th}>Entregue</th><th style={styles.th}>Recebido por</th></tr></thead>
              <tbody>
                {projeto.entregas.map((e, i) => (
                  <tr key={e.id} style={i % 2 === 0 ? styles.trEven : {}}>
                    <td style={styles.td}>{e.dataEntrega || '-'}</td>
                    <td style={styles.td}>{e.itensEntregues || '-'}</td>
                    <td style={styles.td}>{e.recebedorNome || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {projeto.montagens.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Montagem</h3>
            <div style={styles.tabelaContainer}>
              <table style={styles.tabela}>
                <thead><tr><th style={styles.th}>Data da montagem</th><th style={styles.th}>Descrição</th></tr></thead>
                <tbody>
                  {projeto.montagens.map((m, i) => (
                    <tr key={m.id} style={i % 2 === 0 ? styles.trEven : {}}>
                      <td style={styles.td}>{m.dataMontagem || '-'}</td>
                      <td style={styles.td}>{m.descricao || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <hr style={styles.separador} />

        {projeto.jaAceito ? (
          <div style={styles.successContainer}>
            <span style={{ fontSize: 56, marginBottom: 12 }}>✅</span>
            <h2 style={{ color: '#2e7d32', marginBottom: 8 }}>Projeto Aprovado!</h2>
            <p style={{ color: '#555' }}>Aprovado em {new Date(projeto.dataAceite).toLocaleDateString('pt-BR')}{projeto.aceitoPor ? ` por ${projeto.aceitoPor}` : ''}.</p>
            {projeto.garantiaAte && <p style={{ color: '#555' }}>Garantia até {projeto.garantiaAte}.</p>}
            <p style={{ color: '#888', fontSize: 14 }}>O comprovante da aprovação foi enviado por e-mail. Você pode fechar esta página.</p>
          </div>
        ) : (
          <div style={styles.aceiteSection}>
            <h3 style={styles.aceiteTitulo}>Confirmação de Aceite</h3>

            {pessoaJuridica && (
              <div style={styles.campoNome}>
                <label style={styles.label}>CNPJ da empresa:</label>
                <input type="text" inputMode="numeric" value={cnpj} onChange={(e) => setCnpj(mascaraCnpj(e.target.value))} placeholder="00.000.000/0000-00" style={styles.input} disabled={enviando} />
                {somenteDigitos(cnpj).length === 14 && !cnpjValido(cnpj) && <span style={{ color: '#d32f2f', fontSize: 13 }}>CNPJ inválido</span>}
              </div>
            )}

            <div style={styles.campoNome}>
              <label style={styles.label}>{pessoaJuridica ? 'CPF do responsável pela empresa:' : 'CPF:'}</label>
              <input type="text" inputMode="numeric" value={cpf} onChange={(e) => setCpf(mascaraCpf(e.target.value))} placeholder="000.000.000-00" style={styles.input} disabled={enviando} />
              {somenteDigitos(cpf).length === 11 && !cpfValido(cpf) && <span style={{ color: '#d32f2f', fontSize: 13 }}>CPF inválido</span>}
            </div>

            <label style={styles.checkboxLabel}>
              <input type="checkbox" checked={aceito} onChange={(e) => setAceito(e.target.checked)} style={styles.checkbox} disabled={enviando} />
              <span style={styles.checkboxText}>
                Declaro que recebi o projeto #{projeto.projetoId} e aceito o que foi entregue e montado, conforme acima. Sei que o prazo de garantia começa a contar a partir desta aprovação.
              </span>
            </label>

            <button
              onClick={confirmar}
              disabled={!aceito || !documentosValidos || enviando}
              style={{ ...styles.botaoConfirmar, opacity: (!aceito || !documentosValidos || enviando) ? 0.5 : 1, cursor: (!aceito || !documentosValidos || enviando) ? 'not-allowed' : 'pointer' }}
            >
              {enviando ? 'Enviando...' : 'Confirmar Aprovação do Projeto'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
